import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdSearch,
  MdLocalShipping,
  MdRefresh,
  MdEdit,
  MdUpload,
  MdClose,
  MdDescription,
  MdCheckCircle,
} from 'react-icons/md';
import { authenticatedFetch, resolveFileUrl } from '../../lib/api';
import { EUROASIANN_OFFER_LABEL, useDebouncedSearch } from '@euroasiann/shared';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';
import { FileDownloadLink } from '../../components/shared/FileDownloadLink';
import { PageSkeleton } from '@euroasiann/shared/ui';

type ShipmentRow = {
  _id: string;
  orderNumber?: string;
  status: string;
  supplyPort?: string;
  destinationPort?: string;
  currentLocation?: string;
  trackingNumber?: string;
  trackingProvider?: string;
  trackingProviderName?: string;
  estimatedDelivery?: string;
  vesselId?: { name?: string; imoNumber?: string };
  rfqId?: { rfqNumber?: string };
  quotationId?: { quotationNumber?: string };
  podDocuments?: Array<{ fileName?: string; fileUrl: string }>;
  shippingDocuments?: Array<{ fileName?: string; fileUrl: string }>;
  trackingHistory?: Array<{ status: string; timestamp: string; location?: string; notes?: string }>;
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  'in-transit': 'In Transit',
  'at-port': 'At Port',
  'on-vessel': 'On Vessel',
  delivered: 'Delivered',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
};

export function ShipmentsPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    submitNow: submitSearch,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch(searchParams.get('search') || searchParams.get('order') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(searchParams.get('id'));
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selected, setSelected] = useState<ShipmentRow | null>(null);
  const [updateForm, setUpdateForm] = useState({
    shipmentId: '',
    status: '',
    location: '',
    trackingProvider: 'manual',
    trackingProviderName: '',
    trackingNumber: '',
    notes: '',
  });
  const podRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const shippingRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const { data: shipments = [], isLoading, isFetching, isError, error, refetch } = useQuery<ShipmentRow[]>({
    queryKey: ['admin-shipments', statusFilter, submittedSearch],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (submittedSearch) params.append('search', submittedSearch);
      const res = await authenticatedFetch(`/api/v1/admin/shipments?${params.toString()}`, { signal });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch shipments');
      }
      const json = await res.json();
      return json.data?.shipments || (Array.isArray(json.data) ? json.data : []);
    },
    refetchInterval: 30000,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) setExpandedId(id);
    const search = searchParams.get('search') || searchParams.get('order');
    if (search) submitSearch(search);
  }, [searchParams, submitSearch]);

  const handleRefresh = async () => {
    try {
      const result = await refetch();
      if (result.error) throw result.error;
      showToast('Shipments refreshed', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Failed to refresh shipments', 'error');
    }
  };

  const updateTrackingMutation = useMutation({
    mutationFn: async (payload: typeof updateForm) => {
      const res = await authenticatedFetch(`/api/v1/admin/shipments/${payload.shipmentId}/tracking`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed to update tracking');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      showToast('Tracking updated', 'success');
      setShowUpdateModal(false);
      setSelected(null);
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const confirmDeliveryMutation = useMutation({
    mutationFn: async (shipmentId: string) => {
      const res = await authenticatedFetch(`/api/v1/admin/shipments/${shipmentId}/confirm-delivery`, {
        method: 'POST',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to confirm delivery');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      showToast('Delivery confirmed', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const uploadMutation = useMutation({
    mutationFn: async ({
      shipmentId,
      files,
      kind,
    }: {
      shipmentId: string;
      files: File[];
      kind: 'pod' | 'shipping-docs';
    }) => {
      const formData = new FormData();
      const field = kind === 'pod' ? 'podDocuments' : 'shippingDocuments';
      files.forEach((f) => formData.append(field, f));
      const res = await authenticatedFetch(`/api/v1/admin/shipments/${shipmentId}/${kind}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to upload ${kind}`);
      }
      return res.json();
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-shipments'] });
      showToast(vars.kind === 'pod' ? 'POD uploaded' : 'Shipping docs uploaded', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const openUpdate = (shipment: ShipmentRow) => {
    setSelected(shipment);
    setUpdateForm({
      shipmentId: shipment._id,
      status: shipment.status,
      location: shipment.currentLocation || '',
      trackingProvider: shipment.trackingProvider || 'manual',
      trackingProviderName: shipment.trackingProviderName || '',
      trackingNumber: shipment.trackingNumber || '',
      notes: '',
    });
    setShowUpdateModal(true);
  };

  const statusFilters = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'in-transit', label: 'In Transit' },
    { id: 'at-port', label: 'At Port' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'delayed', label: 'Delayed' },
  ];

  return (
    <div
      className={cn(
        'w-full min-w-0 font-sans text-[hsl(var(--foreground))] space-y-6',
        embedded ? '' : 'min-h-0'
      )}
    >
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Logistics</p>
            <h1 className="text-3xl font-black tracking-tight">Shipments</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track {EUROASIANN_OFFER_LABEL} and platform-seller shipments, POD, and shipping documents.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-[#0088cc]/25 transition-colors hover:bg-[#0077b3] disabled:opacity-50"
          >
            <MdRefresh className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      )}

      {embedded && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-[#0088cc]/25 transition-colors hover:bg-[#0077b3] disabled:opacity-50"
          >
            <MdRefresh className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      )}

      <div className="bg-card border rounded-2xl p-4 shadow-sm space-y-4">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search PO / order, vessel, port, tracking…"
            className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-[hsl(var(--background))]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((f) => {
            const active = statusFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setStatusFilter(f.id)}
                aria-pressed={active}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all',
                  active
                    ? 'bg-[#0088cc] text-white border-[#0088cc] shadow-sm shadow-[#0088cc]/25'
                    : 'bg-transparent text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[#0088cc]/50 hover:text-[#0088cc]'
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        {statusFilter !== 'all' && (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Showing filter: <span className="font-bold text-[#0088cc]">{statusLabels[statusFilter] || statusFilter}</span>
            {searchQuery ? (
              <>
                {' '}
                · Search: <span className="font-semibold text-[hsl(var(--foreground))]">{searchQuery}</span>
              </>
            ) : null}
          </p>
        )}
      </div>

      {isLoading && shipments.length === 0 ? (
        <PageSkeleton variant="table" />
      ) : isError ? (
        <div className="bg-card border border-red-500/30 rounded-2xl p-12 text-center">
          <p className="font-bold text-red-400">Failed to load shipments</p>
          <p className="text-sm text-muted-foreground mt-1">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2 text-sm font-bold text-white"
          >
            Try again
          </button>
        </div>
      ) : shipments.length === 0 ? (
        <div className="bg-card border rounded-2xl p-12 text-center">
          <MdLocalShipping className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-bold">No shipments found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {statusFilter === 'all'
              ? 'Initialize tracking from a purchase order after payment is approved.'
              : `No ${statusLabels[statusFilter] || statusFilter} shipments match this filter${searchQuery ? ' and search' : ''}.`}
          </p>
          {(statusFilter !== 'all' || searchQuery) && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter('all');
                clearSearch();
              }}
              className="mt-4 text-sm font-bold text-[#0088cc] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {shipments.map((shipment) => {
            const expanded = expandedId === shipment._id;
            return (
              <div key={shipment._id} className="bg-card border rounded-2xl overflow-hidden shadow-sm">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : shipment._id)}
                  className="w-full text-left p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-muted/30"
                >
                  <div>
                    <p className="font-black text-lg">{shipment.orderNumber || 'Shipment'}</p>
                    <p className="text-sm text-muted-foreground">
                      {shipment.vesselId?.name || '—'} · {shipment.supplyPort || 'Origin'} →{' '}
                      {shipment.destinationPort || 'Destination'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      RFQ {shipment.rfqId?.rfqNumber || '—'} · Quote{' '}
                      {shipment.quotationId?.quotationNumber || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full text-xs font-black uppercase bg-muted">
                      {statusLabels[shipment.status] || shipment.status}
                    </span>
                    <span className="text-xs font-mono text-primary">
                      {shipment.trackingNumber || 'No tracking #'}
                    </span>
                  </div>
                </button>

                {expanded && (
                  <div className="border-t p-5 space-y-5 bg-muted/10">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openUpdate(shipment)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold"
                      >
                        <MdEdit className="w-4 h-4" />
                        Update Tracking
                      </button>
                      {shipment.status !== 'delivered' && shipment.status !== 'cancelled' && (
                        <button
                          type="button"
                          disabled={confirmDeliveryMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                'Confirm delivery for this shipment? This marks it delivered and notifies the customer.'
                              )
                            ) {
                              confirmDeliveryMutation.mutate(shipment._id);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold disabled:opacity-50"
                        >
                          <MdCheckCircle className="w-4 h-4" />
                          {confirmDeliveryMutation.isPending ? 'Confirming…' : 'Confirm Delivery'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => podRefs.current[shipment._id]?.click()}
                        className="inline-flex items-center gap-2 px-4 py-2 border rounded-xl text-xs font-bold"
                      >
                        <MdUpload className="w-4 h-4" />
                        Upload POD
                      </button>
                      <input
                        ref={(el) => {
                          podRefs.current[shipment._id] = el;
                        }}
                        type="file"
                        accept=".pdf,image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = e.target.files ? Array.from(e.target.files) : [];
                          if (files.length) {
                            uploadMutation.mutate({ shipmentId: shipment._id, files, kind: 'pod' });
                          }
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => shippingRefs.current[shipment._id]?.click()}
                        className="inline-flex items-center gap-2 px-4 py-2 border rounded-xl text-xs font-bold"
                      >
                        <MdDescription className="w-4 h-4" />
                        Shipping Docs
                      </button>
                      <input
                        ref={(el) => {
                          shippingRefs.current[shipment._id] = el;
                        }}
                        type="file"
                        accept=".pdf,image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = e.target.files ? Array.from(e.target.files) : [];
                          if (files.length) {
                            uploadMutation.mutate({
                              shipmentId: shipment._id,
                              files,
                              kind: 'shipping-docs',
                            });
                          }
                          e.target.value = '';
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl border bg-background">
                        <p className="text-[10px] font-bold uppercase text-muted-foreground">Location</p>
                        <p className="font-semibold text-sm">{shipment.currentLocation || '—'}</p>
                      </div>
                      <div className="p-3 rounded-xl border bg-background">
                        <p className="text-[10px] font-bold uppercase text-muted-foreground">Est. Delivery</p>
                        <p className="font-semibold text-sm">
                          {shipment.estimatedDelivery
                            ? new Date(shipment.estimatedDelivery).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      <div className="p-3 rounded-xl border bg-background">
                        <p className="text-[10px] font-bold uppercase text-muted-foreground">Carrier</p>
                        <p className="font-semibold text-sm">
                          {shipment.trackingProviderName || shipment.trackingProvider || '—'}
                        </p>
                      </div>
                    </div>

                    {(shipment.podDocuments?.length || 0) > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase text-muted-foreground mb-2">POD</p>
                        <div className="flex flex-wrap gap-2">
                          {shipment.podDocuments!.map((doc, i) => (
                            <FileDownloadLink
                              key={i}
                              href={resolveFileUrl(doc.fileUrl)}
                              fileName={doc.fileName || `POD ${i + 1}`}
                              className="text-xs text-primary font-bold hover:underline"
                              onError={(message) => showToast(message, 'error')}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {(shipment.trackingHistory?.length || 0) > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase text-muted-foreground mb-2">History</p>
                        <ul className="space-y-2">
                          {[...(shipment.trackingHistory || [])]
                            .reverse()
                            .slice(0, 8)
                            .map((h, i) => (
                              <li key={i} className="text-sm border-l-2 border-primary/30 pl-3">
                                <span className="font-bold">{statusLabels[h.status] || h.status}</span>
                                <span className="text-muted-foreground">
                                  {' '}
                                  · {new Date(h.timestamp).toLocaleString()}
                                </span>
                                {h.location && (
                                  <span className="text-muted-foreground"> · {h.location}</span>
                                )}
                                {h.notes && <p className="text-xs text-muted-foreground">{h.notes}</p>}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showUpdateModal && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card border rounded-2xl w-full max-w-lg shadow-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black">Update Tracking</h3>
              <button type="button" onClick={() => setShowUpdateModal(false)}>
                <MdClose className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-xs font-bold uppercase text-muted-foreground">
                Status
                <select
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                >
                  {Object.keys(statusLabels).map((s) => (
                    <option key={s} value={s}>
                      {statusLabels[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs font-bold uppercase text-muted-foreground">
                Location
                <input
                  value={updateForm.location}
                  onChange={(e) => setUpdateForm({ ...updateForm, location: e.target.value })}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs font-bold uppercase text-muted-foreground">
                Tracking number
                <input
                  value={updateForm.trackingNumber}
                  onChange={(e) => setUpdateForm({ ...updateForm, trackingNumber: e.target.value })}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs font-bold uppercase text-muted-foreground">
                Notes
                <textarea
                  value={updateForm.notes}
                  onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                  rows={2}
                  className="mt-1 w-full border rounded-xl px-3 py-2 text-sm resize-none"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={updateTrackingMutation.isPending}
              onClick={() => updateTrackingMutation.mutate(updateForm)}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-bold disabled:opacity-50"
            >
              {updateTrackingMutation.isPending ? 'Saving…' : 'Save Tracking Update'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
